/**
 * GiftPointsDialog - Customer-Portal Self-Service: Punkte verschenken
 *
 * Iter 299 (Welle A): Eingeloggter Kunde kann Punkte an einen anderen Kunden
 * verschenken. Rechnung geht an Schenker (= self), Punkte an Empfänger.
 *
 * Endpoints (alle unter /api/portal/points):
 *   GET  /gift/packages             → verfügbare Punkte-Pakete
 *   GET  /gift/find-recipient?q=…   → Empfänger-Suche (max 8 Treffer, maskiert)
 *   POST /gift                       → Geschenk abschließen
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Gift, Search, X, Check, Mail, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GiftPointsDialog = ({ open, onOpenChange, token, onSuccess }) => {
  const [step, setStep] = useState(1); // 1=Empfänger, 2=Paket/Custom, 3=Bestätigen
  const [packages, setPackages] = useState([]);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [recipientResults, setRecipientResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [mode, setMode] = useState('package'); // 'package' | 'custom'
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [customPoints, setCustomPoints] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('rechnung_combo');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep(1);
      setRecipientSearch('');
      setRecipientResults([]);
      setSelectedRecipient(null);
      setMode('package');
      setSelectedPackageId('');
      setCustomPoints('');
      setCustomAmount('');
      setPaymentMethod('rechnung_combo');
      setNotes('');
      setSuccess(null);
      // Lade Pakete
      axios.get(`${API}/portal/points/gift/packages`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(r => setPackages(r.data || []))
        .catch(() => setPackages([]));
    }
  }, [open, token]);

  // Debounced Empfänger-Suche
  useEffect(() => {
    if (!recipientSearch.trim() || selectedRecipient) {
      setRecipientResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/portal/points/gift/find-recipient`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { q: recipientSearch.trim() },
        });
        setRecipientResults(res.data || []);
      } catch (err) {
        setRecipientResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [recipientSearch, selectedRecipient, token]);

  const handleSubmit = async () => {
    if (!selectedRecipient) {
      toast.error('Bitte Empfänger auswählen');
      return;
    }
    let points, amount;
    if (mode === 'package') {
      const pkg = packages.find(p => p.id === selectedPackageId);
      if (!pkg) {
        toast.error('Bitte Paket auswählen');
        return;
      }
      points = pkg.points_amount;
      amount = pkg.price;
    } else {
      points = parseInt(customPoints, 10);
      amount = parseFloat(customAmount);
      if (!points || points <= 0) {
        toast.error('Bitte gültige Punkte-Anzahl');
        return;
      }
      if (!amount || amount <= 0) {
        toast.error('Bitte gültigen Betrag');
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        recipient_identifier: selectedRecipient.id,
        payment_method: paymentMethod,
        notes: notes || null,
      };
      if (mode === 'package') {
        payload.package_id = selectedPackageId;
      } else {
        payload.points = points;
        payload.amount_chf = amount;
      }
      const res = await axios.post(`${API}/portal/points/gift`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(res.data);
      toast.success(`${points} Punkte an ${res.data.recipient_first_name} verschenkt!`);
      if (onSuccess) onSuccess(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Geschenk konnte nicht abgeschlossen werden');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = selectedRecipient && (
    (mode === 'package' && selectedPackageId) ||
    (mode === 'custom' && customPoints && customAmount)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto" data-testid="gift-points-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-amber-500" />
            Punkte verschenken
          </DialogTitle>
        </DialogHeader>

        {success ? (
          <div className="text-center py-6 space-y-4" data-testid="gift-points-success">
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <Check className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Geschenk versendet!</h3>
              <p className="text-sm text-zinc-500 mt-1">
                {success.points_gifted} Punkte für <strong>{success.recipient_first_name}</strong>
              </p>
            </div>
            {success.notification?.email && (
              <p className="text-xs text-emerald-700 dark:text-emerald-300">
                <Mail className="w-3.5 h-3.5 inline mr-1" />{success.recipient_first_name} wurde per E-Mail informiert
              </p>
            )}
            {success.notification?.whatsapp && (
              <p className="text-xs text-emerald-700 dark:text-emerald-300">
                <MessageCircle className="w-3.5 h-3.5 inline mr-1" />WhatsApp-Benachrichtigung versendet
              </p>
            )}
            <p className="text-xs text-zinc-500">
              Die Rechnung wurde dir zugestellt und ist in deiner Rechnungsübersicht einsehbar.
            </p>
            <Button onClick={() => onOpenChange(false)} className="w-full" data-testid="gift-points-close-btn">
              Fertig
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Step 1: Empfänger */}
            <div className="space-y-2">
              <Label>1. An wen möchtest du verschenken?</Label>
              {selectedRecipient ? (
                <div className="flex items-center justify-between bg-amber-50 dark:bg-amber-950/30 border border-amber-300 rounded p-3">
                  <div>
                    <p className="text-sm font-medium">
                      <Gift className="w-3.5 h-3.5 inline mr-1" />{selectedRecipient.first_name} {selectedRecipient.last_name_initial}
                    </p>
                    <p className="text-xs text-zinc-500">{selectedRecipient.email_masked}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSelectedRecipient(null)}
                    data-testid="gift-points-recipient-clear"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                    <Input
                      type="text"
                      placeholder="E-Mail oder Kundennummer"
                      value={recipientSearch}
                      onChange={(e) => setRecipientSearch(e.target.value)}
                      className="pl-8"
                      data-testid="gift-points-recipient-search"
                    />
                  </div>
                  {searching && <p className="text-xs text-zinc-500">Suche…</p>}
                  {!searching && recipientResults.length > 0 && (
                    <div className="border border-zinc-200 dark:border-zinc-700 rounded max-h-44 overflow-y-auto">
                      {recipientResults.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => {
                            setSelectedRecipient(r);
                            setRecipientSearch('');
                            setRecipientResults([]);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-amber-50 dark:hover:bg-amber-950/30 text-sm border-b last:border-b-0 border-zinc-100 dark:border-zinc-800"
                          data-testid={`gift-points-recipient-option-${r.id}`}
                        >
                          <div className="font-medium">{r.first_name} {r.last_name_initial}</div>
                          <div className="text-xs text-zinc-500">{r.email_masked}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  {!searching && recipientSearch.length >= 3 && recipientResults.length === 0 && (
                    <p className="text-xs text-amber-700">Keine Treffer für „{recipientSearch}"</p>
                  )}
                  {recipientSearch.length > 0 && recipientSearch.length < 3 && (
                    <p className="text-xs text-zinc-500">Mindestens 3 Zeichen eingeben…</p>
                  )}
                </>
              )}
            </div>

            {/* Step 2: Paket oder Custom */}
            <div className="space-y-2">
              <Label>2. Was schenkst du?</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setMode('package')}
                  className={`flex-1 py-2 px-3 rounded border text-sm ${mode === 'package' ? 'bg-amber-500 text-white border-amber-500' : 'border-zinc-300 dark:border-zinc-700'}`}
                  data-testid="gift-points-mode-package"
                >
                  Paket
                </button>
                <button
                  type="button"
                  onClick={() => setMode('custom')}
                  className={`flex-1 py-2 px-3 rounded border text-sm ${mode === 'custom' ? 'bg-amber-500 text-white border-amber-500' : 'border-zinc-300 dark:border-zinc-700'}`}
                  data-testid="gift-points-mode-custom"
                >
                  Eigener Betrag
                </button>
              </div>
              {mode === 'package' ? (
                <Select value={selectedPackageId} onValueChange={setSelectedPackageId}>
                  <SelectTrigger data-testid="gift-points-package-select">
                    <SelectValue placeholder="Paket auswählen…" />
                  </SelectTrigger>
                  <SelectContent>
                    {packages.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} · {p.points_amount} Punkte · CHF {p.price.toFixed(2)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="number"
                    min="1"
                    value={customPoints}
                    onChange={(e) => setCustomPoints(e.target.value)}
                    placeholder="Punkte"
                    data-testid="gift-points-custom-points"
                  />
                  <Input
                    type="number"
                    step="0.05"
                    min="0"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder="CHF"
                    data-testid="gift-points-custom-amount"
                  />
                </div>
              )}
            </div>

            {/* Step 3: Zahlung */}
            <div className="space-y-2">
              <Label>3. Wie zahlst du?</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger data-testid="gift-points-payment-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rechnung_combo">Rechnung / TWINT (per E-Mail)</SelectItem>
                  <SelectItem value="kreditkarte">Gespeicherte Kreditkarte</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Optionale Notiz */}
            <div className="space-y-2">
              <Label>Persönliche Nachricht (optional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="z.B. Frohes Geburtstag!"
                maxLength={200}
                data-testid="gift-points-notes"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="gift-points-cancel-btn">
                Abbrechen
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="gift-points-submit-btn"
              >
                {submitting ? 'Senden…' : 'Geschenk senden'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GiftPointsDialog;
